/**
 * Field mapping utilities for CSV import
 */

/**
 * Common field name mappings for auto-detection
 */
const COMMON_FIELD_MAPPINGS = {
  // Name variations
  'name': 'full_name',
  'full name': 'full_name',
  'fullname': 'full_name',
  'attendee name': 'full_name',
  'participant name': 'full_name',
  'first name': 'full_name', // Could be split later
  'last name': 'full_name',  // Could be combined later
  
  // Email variations
  'email': 'email',
  'email address': 'email',
  'e-mail': 'email',
  'mail': 'email',
  
  // Phone variations
  'phone': 'phone',
  'phone number': 'phone',
  'telephone': 'phone',
  'mobile': 'phone',
  'cell': 'phone',
  'contact': 'phone',
  
  // Organization variations
  'company': 'organization',
  'organization': 'organization',
  'organisation': 'organization',
  'employer': 'organization',
  'workplace': 'organization',
  'business': 'organization',
  'corp': 'organization',
  
  // Position variations
  'position': 'position',
  'title': 'position',
  'job title': 'position',
  'role': 'position',
  'designation': 'position',
  'job': 'position'
};

/**
 * Auto-map CSV columns to custom fields
 * @param {Array} csvHeaders - CSV column headers
 * @param {Array} customFields - Available custom fields
 * @returns {Object} - Mapping object { csvColumn: customField }
 */
export const autoMapFields = (csvHeaders, customFields) => {
  const mappings = {};
  const usedFields = new Set();
  
  csvHeaders.forEach(header => {
    const normalizedHeader = header.toLowerCase().trim();
    let matchedField = null;
    
    // 1. Exact match by field_name
    matchedField = customFields.find(field => 
      field.field_name.toLowerCase() === normalizedHeader && 
      !usedFields.has(field.field_name)
    );
    
    // 2. Exact match by field_label
    if (!matchedField) {
      matchedField = customFields.find(field => 
        field.field_label.toLowerCase() === normalizedHeader && 
        !usedFields.has(field.field_name)
      );
    }
    
    // 3. Common mappings
    if (!matchedField && COMMON_FIELD_MAPPINGS[normalizedHeader]) {
      const mappedName = COMMON_FIELD_MAPPINGS[normalizedHeader];
      matchedField = customFields.find(field => 
        field.field_name === mappedName && 
        !usedFields.has(field.field_name)
      );
    }
    
    // 4. Fuzzy match by field_label (contains)
    if (!matchedField) {
      matchedField = customFields.find(field => {
        const fieldLabel = field.field_label.toLowerCase();
        return (
          (fieldLabel.includes(normalizedHeader) || normalizedHeader.includes(fieldLabel)) &&
          !usedFields.has(field.field_name)
        );
      });
    }
    
    // 5. Fuzzy match by field_name (contains)
    if (!matchedField) {
      matchedField = customFields.find(field => {
        const fieldName = field.field_name.toLowerCase();
        return (
          (fieldName.includes(normalizedHeader) || normalizedHeader.includes(fieldName)) &&
          !usedFields.has(field.field_name)
        );
      });
    }
    
    if (matchedField) {
      mappings[header] = matchedField;
      usedFields.add(matchedField.field_name);
    } else {
      mappings[header] = null; // No match found
    }
  });
  
  return mappings;
};

/**
 * Get mapping statistics
 * @param {Object} mappings - Field mappings
 * @param {Array} customFields - Available custom fields
 * @returns {Object} - Statistics about the mapping
 */
export const getMappingStats = (mappings, customFields) => {
  const totalColumns = Object.keys(mappings).length;
  const mappedColumns = Object.values(mappings).filter(field => field !== null).length;
  const unmappedColumns = totalColumns - mappedColumns;
  
  const requiredFields = customFields.filter(field => field.is_required);
  const mappedRequiredFields = requiredFields.filter(field => 
    Object.values(mappings).some(mapped => mapped?.field_name === field.field_name)
  );
  const missingRequiredFields = requiredFields.filter(field => 
    !Object.values(mappings).some(mapped => mapped?.field_name === field.field_name)
  );
  
  return {
    totalColumns,
    mappedColumns,
    unmappedColumns,
    mappingPercentage: Math.round((mappedColumns / totalColumns) * 100),
    requiredFields: requiredFields.length,
    mappedRequiredFields: mappedRequiredFields.length,
    missingRequiredFields,
    isComplete: missingRequiredFields.length === 0
  };
};

/**
 * Validate field mappings
 * @param {Object} mappings - Field mappings
 * @param {Array} customFields - Available custom fields
 * @returns {Object} - Validation result
 */
export const validateMappings = (mappings, customFields) => {
  const errors = [];
  const warnings = [];
  
  // Filter out null/undefined mappings before validation
  const validMappings = Object.fromEntries(
    Object.entries(mappings).filter(([, field]) => field !== null && field !== undefined && field.field_name)
  );
  
  // Check if we have any mappings at all
  if (Object.keys(validMappings).length === 0) {
    errors.push({
      type: 'no_mappings',
      message: 'No fields are mapped. Please map at least one field before importing.',
      fields: []
    });
    return { isValid: false, errors, warnings, stats: getMappingStats(mappings, customFields) };
  }
  
  const stats = getMappingStats(validMappings, customFields);
  
  // Check for missing required fields
  if (stats.missingRequiredFields.length > 0) {
    errors.push({
      type: 'missing_required',
      message: `Missing required fields: ${stats.missingRequiredFields.map(f => f.field_label).join(', ')}`,
      fields: stats.missingRequiredFields
    });
  }
  
  // Check for duplicate mappings
  const mappedFields = Object.values(validMappings).filter(field => field !== null && field !== undefined);
  const duplicates = mappedFields.filter((field, index) => 
    mappedFields.findIndex(f => f?.field_name === field?.field_name) !== index
  );
  
  if (duplicates.length > 0) {
    warnings.push({
      type: 'duplicate_mapping',
      message: `Multiple columns mapped to same field: ${duplicates.map(f => f.field_label).join(', ')}`,
      fields: duplicates
    });
  }
  
  // Check for unmapped columns
  if (stats.unmappedColumns > 0) {
    const unmappedColumns = Object.keys(mappings).filter(col => mappings[col] === null);
    warnings.push({
      type: 'unmapped_columns',
      message: `${stats.unmappedColumns} columns will be ignored: ${unmappedColumns.join(', ')}`,
      columns: unmappedColumns
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    stats
  };
};

/**
 * Apply field mappings to CSV data
 * @param {Array} csvRows - CSV data rows
 * @param {Object} mappings - Field mappings
 * @returns {Array} - Transformed data ready for import
 */
export const applyMappings = (csvRows, mappings) => {
  console.log('🔄 Applying mappings to', csvRows.length, 'rows');
  console.log('📋 Mappings:', mappings);
  
  return csvRows.map((row, index) => {
    const mappedRow = {
      _originalRowIndex: index + 1, // For error reporting
      _originalData: { ...row }     // Keep original for debugging
    };
    
    // Apply mappings
    Object.entries(mappings).forEach(([csvColumn, customField]) => {
      if (customField && customField.field_name && row.hasOwnProperty(csvColumn)) {
        const rawValue = row[csvColumn];
        const cleanValue = rawValue !== null && rawValue !== undefined 
          ? String(rawValue).trim() 
          : '';
        
        mappedRow[customField.field_name] = {
          field_name: customField.field_name,
          field_label: customField.field_label,
          field_type: customField.field_type,
          value: cleanValue,
          is_required: customField.is_required
        };
        
        // Log mapping for debugging
        if (index === 0) { // Only log first row to avoid spam
          console.log(`📌 Mapped "${csvColumn}" -> "${customField.field_name}": "${cleanValue}"`);
        }
      }
    });
    
    return mappedRow;
  });
};

/**
 * Get suggested mappings based on field types and names
 * @param {Array} csvHeaders - CSV column headers
 * @param {Array} customFields - Available custom fields
 * @returns {Object} - Suggested mappings with confidence scores
 */
export const getSuggestedMappings = (csvHeaders, customFields) => {
  const suggestions = {};
  
  csvHeaders.forEach(header => {
    const candidates = [];
    const normalizedHeader = header.toLowerCase().trim();
    
    customFields.forEach(field => {
      let score = 0;
      
      // Exact matches get highest score
      if (field.field_name.toLowerCase() === normalizedHeader) {
        score = 100;
      } else if (field.field_label.toLowerCase() === normalizedHeader) {
        score = 95;
      }
      // Common mappings get high score
      else if (COMMON_FIELD_MAPPINGS[normalizedHeader] === field.field_name) {
        score = 90;
      }
      // Partial matches get lower scores
      else if (field.field_label.toLowerCase().includes(normalizedHeader)) {
        score = 70;
      } else if (normalizedHeader.includes(field.field_label.toLowerCase())) {
        score = 65;
      } else if (field.field_name.toLowerCase().includes(normalizedHeader)) {
        score = 60;
      } else if (normalizedHeader.includes(field.field_name.toLowerCase())) {
        score = 55;
      }
      
      // Boost score for required fields
      if (field.is_required && score > 0) {
        score += 10;
      }
      
      if (score > 0) {
        candidates.push({ field, score });
      }
    });
    
    // Sort by score and take top candidates
    candidates.sort((a, b) => b.score - a.score);
    
    suggestions[header] = {
      topMatch: candidates[0] || null,
      alternatives: candidates.slice(1, 4), // Top 3 alternatives
      confidence: candidates[0]?.score || 0
    };
  });
  
  return suggestions;
};

/**
 * Generate mapping preview for user review
 * @param {Object} mappings - Field mappings
 * @param {Array} sampleRows - Sample CSV rows for preview
 * @returns {Array} - Preview data showing how mappings will be applied
 */
export const generateMappingPreview = (mappings, sampleRows) => {
  const preview = sampleRows.slice(0, 5).map((row, index) => {
    const previewRow = {
      rowNumber: index + 1,
      mappings: {}
    };
    
    Object.entries(mappings).forEach(([csvColumn, customField]) => {
      previewRow.mappings[csvColumn] = {
        originalValue: row[csvColumn],
        mappedTo: customField ? customField.field_label : 'Not mapped',
        fieldType: customField?.field_type || null,
        isRequired: customField?.is_required || false
      };
    });
    
    return previewRow;
  });
  
  return preview;
};
